import { useCallback, useMemo } from "react";
import { fallbackChannelMeta, HEX_COLOR, type ChannelGroup } from "@shared/domain";
import type { ChannelDTO } from "@shared/types";
import { useChannels } from "./queries";

/*
 * Colours people pick — for a booking source, a meal plan, an extra — are
 * stored as plain hex. On screen they go through `swatch()`, which lifts them
 * a little at night the way the theme's own channel colours do, and text on
 * them takes whichever of cream or ink reads better.
 */

/** A picked hex as a fill: as chosen by day, lifted toward white at night (see --swatch-keep). */
export const swatch = (hex: string): string => `color-mix(in srgb, ${hex} var(--swatch-keep), #fff)`;

/** Cream on dark colours, ink on light ones. */
export function inkOn(hex: string): string {
  if (!HEX_COLOR.test(hex)) return "#FFFDF8";
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.36 ? "#0d1014" : "#FFFDF8";
}

export type ChannelLook = { code: string; label: string; short: string; group: ChannelGroup; active: boolean; builtin: boolean; color: string; ink: string };

function look(code: string, row: ChannelDTO | undefined): ChannelLook {
  const fallback = fallbackChannelMeta(code);
  const hex = row?.color ?? null;
  return {
    code,
    label: row?.label ?? fallback.label,
    short: row?.short ?? fallback.short,
    group: row?.group ?? fallback.group,
    active: row?.active ?? true,
    builtin: row?.builtin ?? true,
    color: hex ? swatch(hex) : fallback.color,
    ink: hex ? inkOn(hex) : "#FFFDF8",
  };
}

export type ChannelLookup = (code: string) => ChannelLook;

/**
 * How each booking source looks, as the business set it. Until the list has
 * loaded the built-in defaults stand in, so a chip never renders blank.
 */
export function useChannelLook(): { lookup: ChannelLookup; channels: ChannelLook[] } {
  const q = useChannels();
  const rows = q.data;
  const byCode = useMemo(() => new Map((rows ?? []).map((r) => [r.code, r])), [rows]);
  const lookup = useCallback<ChannelLookup>((code) => look(code, byCode.get(code)), [byCode]);
  const channels = useMemo(() => (rows ?? []).map((r) => look(r.code, r)), [rows]);
  return { lookup, channels };
}
